import { Component, OnInit, ViewChild } from '@angular/core';
import { Page } from '../shared/page';
import { MatDialog } from '@angular/material';
import { MatchService } from './match.service';
import { Spinplay } from '../shared/spinplay';
import { ElasticDialogComponent } from './elastic-dialog.component';
import { ElasticDialogResponse } from './elastic-dialog-response.model';
import { OkDialogComponent } from '../dialog/ok-dialog.component';
import { DatePipe } from '@angular/common';
import { IsrcPipe } from '../pipes/isrc.pipe';

@Component({
  selector: 'app-match',
  templateUrl: './match.component.html',
  styleUrls: ['./match.component.css']
})
export class MatchComponent implements OnInit {
  search = {
    ccid: '',
    title: '',
    artist: '',
    days: ''
  };
  currentSearchItem = '';
  page: Page = {
    title: 'Match',
    subtitle: ' - find MusicPoint songs and match song metadata to digital catalog results'
  };

  public gridError = false;
  public itemsPerPage = 10;
  public columnDefs;
  public getRowHeight;

  private gridApi;

  constructor(private matchService: MatchService,
    public dialog: MatDialog,
    private datePipe: DatePipe,
    private isrcPipe: IsrcPipe) { }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim();
    filterValue = filterValue.toLowerCase();
    this.gridApi.setQuickFilter(filterValue);

    this.gridOverlay(this.gridApi.getDisplayedRowCount());
  }

  getMatches() {
    this.gridError = false;
    const filter = this.search[this.currentSearchItem];
    const value = filter ? filter.toLowerCase() : '';

    this.gridApi.showLoadingOverlay();

    switch (this.currentSearchItem) {
      case 'ccid':
        if (!this.isValidCCID(value.trim()) || value.trim() === '') {
          this.warningDialog('Invalid CCID, please try again');
        } else {
          this.matchService.getSpinplaysByCCID(value).subscribe(res => {
            // CCID only returns a singe object and the table will not recognize it. This needs to be cast to an array
            const result: Array<Spinplay> = [];
            if (res) {
              result.push(res);
            }
            this.fillGrid(result, 'spins', false);
          }, err => {
            this.fillGrid([], 'spins', true);
          });
        }
        break;
      case 'title':
        this.matchService.getSpinplaysByTitle(value).subscribe(res => {
          this.fillGrid(res, 'spins', false);
        }, err => {
          this.fillGrid([], 'spins', true);
        });
        break;
      case 'artist':
        this.matchService.getSpinplaysByArtist(value).subscribe(res => {
          this.fillGrid(res, 'spins', false);
        }, err => {
          this.fillGrid([], 'spins', true);
        });
        break;
      case 'days':
        this.matchService.getSpinplaysByDays(value).subscribe(res => {
          this.fillGrid(res, 'updatedDate', false);
        }, err => {
          this.fillGrid([], 'updatedDate', true);
        });
        break;
      default:
        this.gridApi.hideOverlay();
    }
  }

  clearMatches() {
    this.currentSearchItem = '';
    this.initializeSearch();
    this.getTopSpinPlays();
  }

  initializeSearch() {
    this.search = {
      ccid: '',
      title: '',
      artist: '',
      days: ''
    };
  }

  changeSearchType(field) {
    this.currentSearchItem = field;
    const value = this.search[field];
    this.initializeSearch();
    this.search[field] = value;
  }

  getTopSpinPlays() {
    this.gridError = false;
    this.gridApi.showLoadingOverlay();
    this.matchService.getTopSpinplays().subscribe(
      res => {
        this.fillGrid(res, 'spins', false);
      },
      error => {
        this.fillGrid([], 'spins', true);
      });
  }

  openDialog(row) {
    this.dialog.open(ElasticDialogComponent, {
      width: '98vw',
      maxWidth: '1280px',
      data: row.data
    }).afterClosed().subscribe((res: ElasticDialogResponse) => {
      if (res && res.isMatch) {
        if (this.currentSearchItem && this.currentSearchItem !== 'ccid') {
          const selectedRow = this.gridApi.getSelectedRows();
          this.gridApi.updateRowData({ remove: selectedRow });
        } else {
          this.getTopSpinPlays();
        }

        this.dialog.open(OkDialogComponent, {
          width: '500px',
          data: { 'message': res.message }
        });
      }
    });

  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.getTopSpinPlays();
  }

  fillGrid(spinPlays: Spinplay[], orderedBy: String, hasError: boolean) {
    this.gridApi.setRowData(spinPlays);

    if (hasError) {
      this.gridApi.hideOverlay();
      this.gridError = true;
    } else {
      this.gridOverlay(spinPlays.length);
    }

    this.gridApi.paginationGoToPage(0);
    this.gridApi.setSortModel([{
      colId: orderedBy,
      sort: 'desc'
    }]);
  }

  gridOverlay(rowCount: number) {
    if (rowCount === 0) {
      this.gridApi.showNoRowsOverlay();
    } else {
      this.gridApi.hideOverlay();
    }
  }

  changeItemsPerPage() {
    this.gridApi.paginationSetPageSize(this.itemsPerPage);
  }

  private isValidCCID(value: string): boolean {
    const ccidValue = value.trim();
    if (ccidValue !== '') {
      const regex = /^[0-9]+$/;
      return regex.test(ccidValue);
    }
    return true;
  }
  private warningDialog(message: string) {
    this.dialog.open(OkDialogComponent, {
      width: '500px',
      data: { 'message': message }
    });
  }

  ngOnInit() {
    this.columnDefs = [
      {
        headerName: 'Spins',
        field: 'spins',
        sort: 'desc',
        width: 85,
        lockPosition: true
      },
      {
        headerName: 'CCID',
        field: 'ccid',
        width: 75,
        lockPosition: true
      },
      {
        headerName: 'Title',
        field: 'title',
        width: 130,
        lockPosition: true
      },
      {
        headerName: 'Artist',
        field: 'artist',
        width: 120,
        lockPosition: true
      },
      {
        headerName: 'Album',
        field: 'album',
        width: 110,
        lockPosition: true
      },
      {
        headerName: 'Duration',
        field: 'duration',
        width: 70,
        lockPosition: true
      },
      {
        headerName: 'Version',
        field: 'version',
        width: 65,
        lockPosition: true
      },
      {
        headerName: 'Genre',
        field: 'format',
        width: 90,
        lockPosition: true
      },
      {
        headerName: 'Label',
        field: 'label',
        width: 60,
        lockPosition: true
      },
      {
        headerName: 'Created  Date',
        field: 'createdDate',
        width: 90,
        cellRenderer: (params) => {
          return this.datePipe.transform(params.value, 'yyyy-MM-dd hh:mm:ss');
        },
        lockPosition: true
      },
      {
        headerName: 'Updated Date',
        field: 'updatedDate',
        width: 90,
        cellRenderer: (params) => {
          return this.datePipe.transform(params.value, 'yyyy-MM-dd hh:mm:ss');
        },
        lockPosition: true
      },
      {
        headerName: 'ISRC',
        field: 'isrc',
        width: 125,
        cellRenderer: (params) => {
          return this.isrcPipe.transform(params.value);
        },
        lockPosition: true
      }
    ];

    this.getRowHeight = function (params) {
      return 55;
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/match/match.component.ts